/**
 * FilesDrawer — Phase 2e (vibe-designer 1.128.0).
 *
 * Left-edge slide-in drawer that hosts the Explorer (FilesPanel) on touch.
 * Visibility gated on uiStore.filesDrawerOpen. Master plan §3.1: "Files
 * Panel (Explorer): on phone, hide by default. Drawer-style slide-in from
 * the left."
 *
 * Composition pattern: accepts a single `children: React.ReactNode` so the
 * parent AppShell can pre-materialize <FilesPanel /> once and pass it
 * here. The same instance flows to FilesDrawer (touch) OR DesktopPanelGroup
 * (desktop) — never both, because isTouch is locked per session.
 *
 * Z-index layering (extends MobileMenuDrawer ladder):
 *   • MindChatWidget FAB:        removed 1.555.0 (trigger moved to TopBar)
 *   • MindChatWidget backdrop:   z-[9999]
 *   • MindChatWidget panel:      z-[10000]
 *   • MindChatWidget resize:     z-[10001]
 *   • MobileMenuDrawer backdrop: z-[10002]
 *   • MobileMenuDrawer panel:    z-[10003]
 *   • FilesDrawer backdrop:      z-[10004]  ← this file
 *   • FilesDrawer panel:         z-[10005]
 *   • Top-bar menu backdrop:     z-[10006]  (SettingsMenu / UserMenu / project-switcher)
 *   • Top-bar menu panel:        z-[10007]  (SettingsMenu / UserMenu / project-switcher)
 *
 * The top-bar chrome menus MUST sit above the embedded briefing-room (007)
 * module, whose internal fixed overlays reach z-1000 (PdfViewerModal),
 * z-195/200 (banners), z-100 (drawers), z-60 (dispatch). They were left at
 * the legacy z-40/50 and the module content painted over them — fixed
 * 2026-06-16 by migrating all three top-bar menus to this band.
 *
 * FilesDrawer renders ABOVE MobileMenuDrawer intentionally — if both
 * happened to be open, file actions win. In practice no UI path opens
 * both simultaneously, but the ordering is documented for future maintainers.
 *
 * Escape-key close is deferred to Phase 2f (parity with MobileMenuDrawer).
 * Backdrop click + explicit close button cover the primary close paths.
 */
import { useEffect } from 'react';
import { Folder } from 'lucide-react';
import { useUIStore } from '@/stores/uiStore';

interface FilesDrawerProps {
  children: React.ReactNode;
}

export function FilesDrawer({ children }: FilesDrawerProps) {
  const open = useUIStore((s) => s.filesDrawerOpen);
  const setFilesDrawerOpen = useUIStore((s) => s.setFilesDrawerOpen);

  // 1.129.0 Phase 2f: Escape-key closes the drawer. Adapted from
  // MindChatWidget.tsx:189-196 — gate inside the effect body so the
  // listener only attaches when drawer is open (mirrors MobileMenuDrawer
  // Phase 2f addition; both drawer Escape paths are independent — both
  // closing-a-closed-drawer is a no-op).
  useEffect(() => {
    if (!open) return;
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFilesDrawerOpen(false);
    };
    document.addEventListener('keydown', onKeydown);
    return () => document.removeEventListener('keydown', onKeydown);
  }, [open, setFilesDrawerOpen]);

  if (!open) return null;

  return (
    <div
      data-testid="files-drawer-backdrop"
      onClick={() => setFilesDrawerOpen(false)}
      className="fixed inset-0 z-[10004] bg-black/40"
    >
      <div
        data-testid="files-drawer"
        onClick={(e) => e.stopPropagation()}
        className="fixed top-0 left-0 h-full w-80 max-w-[85vw] bg-vibe-bg-secondary border-r border-vibe-border shadow-lg z-[10005] animate-slide-in-left flex flex-col"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-vibe-border">
          <div className="flex items-center gap-2 text-vibe-text">
            <Folder className="w-4 h-4" />
            <span className="font-semibold">Files</span>
          </div>
          <button
            data-testid="files-drawer-close"
            onClick={() => setFilesDrawerOpen(false)}
            aria-label="Close files drawer"
            className="text-xs text-vibe-text-secondary hover:text-vibe-text [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-hidden">{children}</div>
      </div>
    </div>
  );
}

export default FilesDrawer;
